import { type FormEvent, useEffect, useMemo, useState } from 'react';

import type { PageRead } from '@/api/client';
import { LIMITS } from '@/config';
import { useAuthContext } from '@/features/auth/AuthContext';
import { useT } from '@/i18n/LocaleProvider';

import { createProposeApiClient, type MediaUploadResponse, type ProposalResponse } from './api';
import { MarkdownPreview } from './MarkdownPreview';

export type ProposeEditorProps = {
  mode: 'new' | 'edit';
  initialSlug?: string;
  initialTitle?: string;
  language?: string;
  translationGroup?: string;
  isPageLoading?: boolean;
  page?: PageRead | null;
  onCancel?: () => void;
  onLogin?: () => void;
  onRegister?: () => void;
  onSubmitted?: (proposal: ProposalResponse) => void;
};

export function ProposeEditor({
  mode,
  initialTitle,
  language,
  translationGroup,
  isPageLoading = false,
  page,
  onCancel,
  onLogin,
  onRegister,
  onSubmitted,
}: ProposeEditorProps) {
  const t = useT();
  const auth = useAuthContext();
  const api = useMemo(() => createProposeApiClient(() => auth.token), [auth.token]);
  const [title, setTitle] = useState('');
  const [markdown, setMarkdown] = useState('');
  const [summary, setSummary] = useState('');
  // Comma-separated categories (the page taxonomy); parsed to a list on submit.
  const [categoriesInput, setCategoriesInput] = useState('');
  // The shared cross-language key (translation_group) that links this page to its
  // translations. Prefilled when creating a translation from the language switcher.
  const [translationKey, setTranslationKey] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [submittedProposal, setSubmittedProposal] = useState<ProposalResponse | null>(null);
  const [uploadedMedia, setUploadedMedia] = useState<MediaUploadResponse[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setSubmitError(null);
    setUploadError(null);
    setSubmittedProposal(null);
    setUploadedMedia([]);
    setSummary('');

    if (mode === 'edit' && page) {
      setTitle(page.title);
      setMarkdown(page.markdown ?? '');
      setCategoriesInput((page.categories ?? []).join(', '));
      setTranslationKey(page.translation_group ?? '');
      return;
    }

    if (mode === 'new') {
      setTitle(initialTitle?.trim() ?? '');
      setMarkdown('');
      setCategoriesInput('');
      setTranslationKey(translationGroup ?? '');
    }
  }, [mode, page, initialTitle, translationGroup]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!auth.isAuthenticated) {
      setSubmitError(t('propose_login_required'));
      return;
    }

    setSubmitError(null);
    setSubmittedProposal(null);
    setIsSubmitting(true);

    try {
      const categories = parseCategories(categoriesInput);

      const proposal = await api.createProposal(
        mode === 'new'
          ? {
              kind: 'new',
              title,
              proposed_markdown: markdown,
              summary,
              language: language || undefined,
              translation_group: translationKey.trim() || undefined,
              categories,
            }
          : {
              kind: 'edit',
              page_id: page?.id,
              base_revision: page?.current_revision,
              proposed_markdown: markdown,
              summary,
              categories,
            },
      );

      setSubmittedProposal(proposal);
      onSubmitted?.(proposal);
    } catch (error) {
      if (isUnauthorizedError(error)) {
        setSubmitError(t('error_session_expired'));
        auth.onUnauthorized();
        return;
      }

      setSubmitError(errorMessage(error, t('propose_error_submit')));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleUpload(file: File | undefined) {
    if (!file) {
      return;
    }

    setUploadError(null);
    setIsUploading(true);

    try {
      const media = await api.uploadMedia({
        file,
        pageID: mode === 'edit' ? page?.id : undefined,
      });
      setUploadedMedia((current) => [media, ...current]);
      setMarkdown((current) => appendImageMarkdown(current, file.name, media.url));
    } catch (error) {
      if (isUnauthorizedError(error)) {
        auth.onUnauthorized();
        return;
      }

      setUploadError(errorMessage(error, t('propose_error_upload')));
    } finally {
      setIsUploading(false);
    }
  }

  const isEditUnavailable = mode === 'edit' && !page;
  const canSubmit =
    auth.isAuthenticated &&
    !isSubmitting &&
    !isPageLoading &&
    !isEditUnavailable &&
    markdown.trim() !== '' &&
    (mode === 'edit' || title.trim() !== '');

  const headingText =
    mode === 'new'
      ? t('propose_new_title')
      : t('propose_edit_title', { title: page?.title ?? title });

  return (
    <section className="editor-workspace" aria-labelledby="proposal-title">
      <header className="editor-header">
        <div>
          <p>{mode === 'new' ? t('propose_new_label') : t('propose_edit_label')}</p>
          <h1 id="proposal-title">{headingText}</h1>
        </div>

        <div className="editor-header-actions">
          <button type="button" onClick={onCancel}>
            {t('propose_cancel')}
          </button>
        </div>
      </header>

      {!auth.isAuthenticated ? (
        <div className="auth-required">
          <strong>{t('auth_contributor_required')}</strong>
          <span>{t('propose_hint')}</span>
          <div className="form-actions">
            <button className="primary-action" type="button" onClick={onLogin}>
              {t('auth_login')}
            </button>
            <button className="text-action" type="button" onClick={onRegister}>
              {t('auth_register')}
            </button>
          </div>
        </div>
      ) : null}

      {isPageLoading ? <div className="empty-note">{t('propose_page_loading')}</div> : null}
      {isEditUnavailable && !isPageLoading ? (
        <div className="empty-note">{t('propose_page_not_found')}</div>
      ) : null}

      <form className="proposal-editor" onSubmit={handleSubmit}>
        <div className="proposal-fields">
          {mode === 'new' ? (
            <div className="proposal-meta-grid">
              <label>
                <span>{t('propose_title_label')}</span>
                <input value={title} onChange={(event) => setTitle(event.target.value)} required />
              </label>

              <label>
                <span>{t('propose_translation_key_label')}</span>
                <input
                  value={translationKey}
                  onChange={(event) => setTranslationKey(event.target.value)}
                  placeholder={t('propose_translation_key_placeholder')}
                />
              </label>
            </div>
          ) : (
            <div className="proposal-target" data-testid="proposal-target">
              <span>{t('propose_target_page')}</span>
              <strong>{page?.title ?? title}</strong>
              {page?.current_revision ? <small>{page.current_revision}</small> : null}
            </div>
          )}

          <label>
            <span>{t('propose_categories_label')}</span>
            <input
              value={categoriesInput}
              onChange={(event) => setCategoriesInput(event.target.value)}
              placeholder={t('propose_categories_placeholder')}
            />
          </label>

          <label>
            <span>{t('propose_summary_label')}</span>
            <input
              value={summary}
              maxLength={LIMITS.titleMaxLength}
              onChange={(event) => setSummary(event.target.value)}
              placeholder={t('propose_summary_placeholder')}
            />
          </label>

          <label className="markdown-field">
            <span>{t('propose_markdown_label')}</span>
            <textarea
              aria-label={t('propose_markdown_label')}
              value={markdown}
              onChange={(event) => setMarkdown(event.target.value)}
              spellCheck={false}
              required
            />
          </label>

          <div className="media-widget">
            <label>
              <span>{t('propose_image_label')}</span>
              <input
                accept="image/jpeg,image/png,image/webp"
                aria-label={t('propose_upload_label')}
                type="file"
                onChange={(event) => {
                  void handleUpload(event.target.files?.[0]);
                  event.target.value = '';
                }}
              />
            </label>
            <span>{isUploading ? t('propose_uploading') : t('propose_image_hint')}</span>
            {uploadError ? <div className="form-error">{uploadError}</div> : null}
            {uploadedMedia.length > 0 ? (
              <ul className="uploaded-media-list">
                {uploadedMedia.map((media) => (
                  <li key={media.key}>
                    <a href={media.url}>{media.key}</a>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>

        <section className="proposal-preview" aria-label={t('propose_preview_label')}>
          <div className="proposal-preview-header">
            <span>{t('propose_preview_label')}</span>
          </div>
          <MarkdownPreview markdown={markdown} />
        </section>

        {submitError ? <div className="form-error proposal-submit-error">{submitError}</div> : null}

        {submittedProposal ? (
          <div className="proposal-status" data-testid="proposal-status">
            <strong>{t('propose_awaiting')}</strong>
            <span>{submittedProposal.id}</span>
          </div>
        ) : null}

        <div className="proposal-submit-row">
          <button className="primary-action" type="submit" disabled={!canSubmit}>
            {isSubmitting ? t('propose_submitting') : t('propose_submit')}
          </button>
        </div>
      </form>
    </section>
  );
}

function isUnauthorizedError(error: unknown): boolean {
  return (
    Boolean(error) &&
    typeof error === 'object' &&
    'status' in (error as object) &&
    (error as { status: number }).status === 401
  );
}

function appendImageMarkdown(markdown: string, fileName: string, url: string): string {
  const alt = fileName.replace(/\.[^.]+$/, '').trim() || 'image';
  const imageMarkdown = `![${alt}](${url})`;
  const trimmed = markdown.trimEnd();

  return trimmed ? `${trimmed}\n\n${imageMarkdown}\n` : `${imageMarkdown}\n`;
}

// parseCategories splits the comma-separated categories input into a trimmed,
// de-duplicated list (undefined when empty so the field is omitted from the payload).
function parseCategories(value: string): string[] | undefined {
  const seen = new Set<string>();
  const list: string[] = [];

  for (const raw of value.split(',')) {
    const name = raw.trim();
    if (name && !seen.has(name)) {
      seen.add(name);
      list.push(name);
    }
  }

  return list.length > 0 ? list : undefined;
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}
